import { lazy, Suspense, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/esm/ExpandMore';
import KeyboardArrowLeftIcon from '@mui/icons-material/esm/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/esm/KeyboardArrowRight';
import LineChart from '../../components/charts/LineChart';
import PersonnelFolderCard from '../../components/field/PersonnelFolderCard';
import { Task } from '../../services/api/tasksService';
import { PurchaseOrder } from '../../services/api/procurementService';
import { InventoryMovementItem } from '../../services/api/inventoryMovementService';
import { InventorySummaryRow } from '../../services/api/resourceService';
import { VarianceRow } from '../../services/api/financialsService';
import { AccountsTransaction } from '../../services/api/roleWorkspaceService';
import useExecutiveFieldData, { kpiByName } from './useExecutiveFieldData';
import { ACCOUNTS_PERSONNEL_QUERY_KEY, executiveFieldPersonnel, fieldPersonnelById } from '../../routes/fieldPersonnel';
import { RootState } from '../../store';

const ExecutiveEstateProgress = lazy(() => import('../../components/field/ExecutiveEstateProgress'));

type ExecutiveFieldView = 'home' | 'estate-progress' | 'financial' | 'construction-tasks' | 'supervisor-inspections' | 'stores' | 'procurement';
type AnalyticsPeriodMode = 'month' | 'week' | 'day';

interface ExecutiveFieldModePageProps {
  view?: ExecutiveFieldView;
}

interface IndicatorView {
  value: string;
  status: string;
  note: string;
}

interface IndicatorCard {
  label: string;
  views: IndicatorView[];
}

interface CrewCompletionDataset {
  label: string;
  data: number[];
  borderColor: string;
}

const money = (value: number) =>
  new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD', maximumFractionDigits: 0 }).format(value);

const statusKey = (task: Task) => String(task.lifecycleStatus || task.status || '').toUpperCase();

const COMPLETED_STATUSES = ['COMPLETED', 'INSPECTION_PASSED', 'CLOSED'];
const OPEN_PO_STATUSES: PurchaseOrder['status'][] = ['draft', 'pending_approval', 'approved', 'sent', 'confirmed', 'partial_received'];

const kpiNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const monthLabel = (isoDate: string): string => {
  const date = new Date(isoDate);
  return Number.isNaN(date.getTime()) ? isoDate : date.toLocaleDateString('en-JM', { month: 'short' });
};

const isoDateKey = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

const dateInputValue = (value: Date): string => value.toISOString().slice(0, 10);

const addDays = (value: Date, days: number): Date => {
  const next = new Date(value);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

const monthKey = (value = new Date()): string => value.toISOString().slice(0, 7);

const addMonths = (value: Date, months: number): Date => {
  const next = new Date(value);
  next.setUTCMonth(next.getUTCMonth() + months);
  return next;
};

const dateOnlyPattern = /^(\d{4})-(\d{2})-(\d{2})$/;

const dateOnlyParts = (value?: string | Date | null) => {
  if (typeof value !== 'string') return null;
  const match = value.match(dateOnlyPattern);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
};

const dateOnlyTime = (value?: string | Date | null): number | null => {
  const parts = dateOnlyParts(value);
  return parts ? new Date(parts.year, parts.month - 1, parts.day).getTime() : null;
};

const monthKeyForDate = (value?: string | Date | null): string => {
  const parts = dateOnlyParts(value);
  if (parts) return `${parts.year}-${String(parts.month).padStart(2, '0')}`;
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toISOString().slice(0, 7) : 'undated';
};

const monthBounds = (key: string): { start: string; end: string } => {
  const [year, month] = key.split('-').map(Number);
  const safeYear = Number.isFinite(year) ? year : new Date().getUTCFullYear();
  const safeMonth = Number.isFinite(month) ? month - 1 : new Date().getUTCMonth();
  return {
    start: dateInputValue(new Date(Date.UTC(safeYear, safeMonth, 1))),
    end: dateInputValue(new Date(Date.UTC(safeYear, safeMonth + 1, 0))),
  };
};

const monthOptions = (year = new Date().getUTCFullYear()): Array<{ value: string; label: string }> =>
  Array.from({ length: 12 }, (_, index) => {
    const value = `${year}-${String(index + 1).padStart(2, '0')}`;
    return {
      value,
      label: new Date(Date.UTC(year, index, 1)).toLocaleDateString('en-JM', { month: 'long', year: 'numeric' }),
    };
  });

const shortMonthLabelFor = (key: string): string => {
  const date = new Date(`${key}-01T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? key : date.toLocaleDateString('en-JM', { month: 'short', year: '2-digit' });
};

const monthKeysBetween = (startDate: string, endDate: string): string[] => {
  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  const first = start <= end ? start : end;
  const last = start <= end ? end : start;
  const cursor = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1));
  const final = new Date(Date.UTC(last.getUTCFullYear(), last.getUTCMonth(), 1));
  const keys: string[] = [];
  for (let current = cursor; current <= final; current = addMonths(current, 1)) {
    keys.push(monthKey(current));
    if (keys.length > 72) break;
  }
  return keys;
};

const periodBounds = (mode: AnalyticsPeriodMode, month: string, weekStart: string, day: string): { start: string; end: string } => {
  if (mode === 'day') {
    const selectedDay = day || dateInputValue(new Date());
    return { start: selectedDay, end: selectedDay };
  }
  if (mode === 'week') {
    const start = weekStart || dateInputValue(addDays(new Date(), -6));
    const startDate = new Date(`${start}T00:00:00Z`);
    if (Number.isNaN(startDate.getTime())) {
      const fallback = dateInputValue(addDays(new Date(), -6));
      return { start: fallback, end: dateInputValue(new Date()) };
    }
    return { start, end: dateInputValue(addDays(startDate, 6)) };
  }
  return monthBounds(month || monthKey());
};

const dateLabelsBetween = (startDate: string, endDate: string): string[] => {
  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  const labels: string[] = [];
  for (let current = start; current <= end; current = addDays(current, 1)) {
    labels.push(dateInputValue(current));
    if (labels.length > 370) break;
  }
  return labels;
};

const shortDateLabel = (isoDate: string): string => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? isoDate : date.toLocaleDateString('en-JM', { month: 'short', day: 'numeric' });
};

const completedDateForTask = (task: Task): string | null =>
  isoDateKey(task.completionDate ?? task.completedAt ?? task.updatedAt ?? task.createdAt);

const inspectionDateForTask = (task: Task): string | null =>
  isoDateKey(task.reviewedAt ?? task.completionDate ?? task.completedAt ?? task.updatedAt ?? task.createdAt);

const inspectionResultForTask = (task: Task): 'passed' | 'failed' | null => {
  const status = statusKey(task);
  const verdict = String(task.reviewVerdict ?? '').toLowerCase();
  if (status === 'INSPECTION_PASSED' || verdict === 'approved' || verdict === 'passed') return 'passed';
  if (status === 'INSPECTION_FAILED' || verdict === 'failed' || verdict === 'remedial') return 'failed';
  return null;
};

const completedWorkStatus = (task: Task): boolean => COMPLETED_STATUSES.includes(statusKey(task));

const inventoryGroupForRow = (row: InventorySummaryRow): string => row.category || row.resourceType || 'Uncategorized';

const movementDate = (movement: InventoryMovementItem): string | null => isoDateKey(movement.createdAt);

const movementSignedQuantity = (movement: InventoryMovementItem): number => {
  const quantity = Math.abs(Number(movement.quantity || 0));
  const type = String(movement.movementType || '').toLowerCase();
  if (['receipt', 'return', 'transfer_in'].includes(type)) return quantity;
  if (['issue', 'consumption', 'transfer_out', 'damage', 'theft', 'write_off'].includes(type)) return -quantity;
  return Number(movement.quantity || 0);
};

const purchaseOrderDate = (po: PurchaseOrder): string | null =>
  isoDateKey(po.createdAt ?? po.submittedAt ?? po.expectedDeliveryDate ?? po.updatedAt);

const chartTickStep = (maxValue: number): number => {
  if (maxValue <= 6) return 2;
  if (maxValue <= 20) return 4;
  if (maxValue <= 50) return 10;
  if (maxValue <= 100) return 20;
  if (maxValue <= 250) return 50;
  if (maxValue <= 500) return 100;
  return Math.ceil(maxValue / 6 / 100) * 100;
};

function CrewCompletionSvgChart({
  labels,
  datasets,
  spanSinglePoint = false,
  ariaLabel = 'Line graph showing daily completed work by crew',
}: {
  labels: string[];
  datasets: CrewCompletionDataset[];
  spanSinglePoint?: boolean;
  ariaLabel?: string;
}) {
  const left = 64;
  const top = 26;
  const width = 790;
  const height = 265;
  const bottom = top + height;
  const highestValue = Math.max(0, ...datasets.flatMap((row) => row.data));
  const tickStep = chartTickStep(Math.max(6, highestValue));
  const maxValue = Math.max(6, Math.ceil(highestValue / tickStep) * tickStep);
  const yTicks = Array.from({ length: Math.floor(maxValue / tickStep) + 1 }, (_, index) => index * tickStep);
  const xFor = (index: number) => (labels.length <= 1 ? 459 : left + (index * (width / (labels.length - 1))));
  const yFor = (value: number) => top + height - ((value / maxValue) * height);
  const pointPath = (values: number[]) => (
    values.length === 1 && spanSinglePoint
      ? `M ${left.toFixed(1)} ${yFor(values[0]).toFixed(1)} L ${(left + width).toFixed(1)} ${yFor(values[0]).toFixed(1)}`
      : values.length <= 1
      ? ''
      : values.map((value, index) => `${index === 0 ? 'M' : 'L'} ${xFor(index).toFixed(1)} ${yFor(value).toFixed(1)}`).join(' ')
  );

  return (
    <Box sx={{ px: 1.5, pb: 1.75, pt: 0.75, overflowX: 'auto' }}>
      <Box
        component="svg"
        viewBox="0 0 900 360"
        role="img"
        aria-label={ariaLabel}
        sx={{ width: '100%', minWidth: 720, height: 360, display: 'block' }}
      >
        {yTicks.map((tick) => {
          const y = bottom - ((tick / maxValue) * height);
          return (
            <g key={tick}>
              <line x1={left} y1={y} x2={854} y2={y} stroke="#e7ebf0" strokeWidth={1} />
              <text x={22} y={y + 4} fill="#647282" fontSize={11} fontWeight={750}>{tick}</text>
            </g>
          );
        })}
        <line x1={left} y1={bottom} x2={854} y2={bottom} stroke="#aab4c0" strokeWidth={1.2} />
        <line x1={left} y1={top} x2={left} y2={bottom} stroke="#aab4c0" strokeWidth={1.2} />
        {labels.map((label, index) => {
          const showLabel = labels.length <= 14 || index === 0 || index === labels.length - 1 || index % 2 === 0;
          return showLabel ? (
            <text key={`${label}-${index}`} x={xFor(index) - 18} y={326} fill="#647282" fontSize={11} fontWeight={750}>{label}</text>
          ) : null;
        })}
        {datasets.map((dataset) => (
          <g key={dataset.label}>
            {pointPath(dataset.data) && (
              <path
                d={pointPath(dataset.data)}
                fill="none"
                stroke={dataset.borderColor}
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {dataset.data.map((value, index) => (
              <circle
                key={`${dataset.label}-${index}`}
                cx={xFor(index)}
                cy={yFor(value)}
                r={dataset.data.length === 1 ? 7 : 4.5}
                fill={dataset.borderColor}
                stroke="#fff"
                strokeWidth={2}
              >
                <title>{labels[index]} - {dataset.label}: {value} completed</title>
              </circle>
            ))}
          </g>
        ))}
      </Box>
    </Box>
  );
}

function SupplierDeliveryBarChart({ labels, dataset }: { labels: string[]; dataset: CrewCompletionDataset }) {
  const left = 64;
  const top = 26;
  const width = 790;
  const height = 265;
  const bottom = top + height;
  const highestValue = Math.max(0, ...dataset.data);
  const tickStep = chartTickStep(Math.max(6, highestValue));
  const maxValue = Math.max(6, Math.ceil(highestValue / tickStep) * tickStep);
  const yTicks = Array.from({ length: Math.floor(maxValue / tickStep) + 1 }, (_, index) => index * tickStep);
  const slotWidth = labels.length <= 0 ? width : width / labels.length;
  const barWidth = Math.max(8, Math.min(24, slotWidth * 0.56));
  const xFor = (index: number) => left + (index * slotWidth) + ((slotWidth - barWidth) / 2);
  const yFor = (value: number) => top + height - ((value / maxValue) * height);

  return (
    <Box sx={{ px: 1.5, pb: 1.75, pt: 0.75, overflowX: 'auto' }}>
      <Box
        component="svg"
        viewBox="0 0 900 360"
        role="img"
        aria-label="Bar graph showing purchase orders placed by supplier"
        sx={{ width: '100%', minWidth: 720, height: 360, display: 'block' }}
      >
        {yTicks.map((tick) => {
          const y = bottom - ((tick / maxValue) * height);
          return (
            <g key={tick}>
              <line x1={left} y1={y} x2={854} y2={y} stroke="#e7ebf0" strokeWidth={1} />
              <text x={22} y={y + 4} fill="#647282" fontSize={11} fontWeight={750}>{tick}</text>
            </g>
          );
        })}
        <line x1={left} y1={bottom} x2={854} y2={bottom} stroke="#aab4c0" strokeWidth={1.2} />
        <line x1={left} y1={top} x2={left} y2={bottom} stroke="#aab4c0" strokeWidth={1.2} />
        {dataset.data.map((value, index) => {
          const y = yFor(value);
          const barHeight = Math.max(0, bottom - y);
          return (
            <rect
              key={`${dataset.label}-${labels[index]}-${index}`}
              x={xFor(index)}
              y={y}
              width={barWidth}
              height={barHeight}
              fill={dataset.borderColor}
              opacity={0.82}
              rx={3}
            >
              <title>{labels[index]} - {dataset.label}: {value} purchase orders placed</title>
            </rect>
          );
        })}
        {labels.map((label, index) => {
          const showLabel = labels.length <= 14 || index === 0 || index === labels.length - 1 || index % 2 === 0;
          return showLabel ? (
            <text key={`${label}-${index}`} x={left + (index * slotWidth) + (slotWidth / 2) - 18} y={326} fill="#647282" fontSize={11} fontWeight={750}>{label}</text>
          ) : null;
        })}
      </Box>
    </Box>
  );
}

function MonthlySpendingBarChart({ labels, values }: { labels: string[]; values: number[] }) {
  const left = 92;
  const top = 24;
  const width = Math.max(790, labels.length * 72);
  const height = 265;
  const bottom = top + height;
  const viewWidth = left + width + 24;
  const highestValue = Math.max(0, ...values);
  const maxValue = Math.max(1, highestValue);
  const slotWidth = labels.length > 0 ? width / labels.length : width;
  const barWidth = Math.max(18, Math.min(46, slotWidth * 0.58));
  const yFor = (value: number) => top + height - ((value / maxValue) * height);
  const yTicks = [0, maxValue / 2, maxValue];

  return (
    <Box sx={{ px: 1.5, pb: 1.75, pt: 0.75, overflowX: 'auto' }}>
      <Box
        component="svg"
        viewBox={`0 0 ${viewWidth} 360`}
        role="img"
        aria-label="Bar chart showing total money spent for each month"
        sx={{ width: '100%', minWidth: viewWidth, height: 360, display: 'block' }}
      >
        {yTicks.map((tick) => {
          const y = yFor(tick);
          return (
            <g key={tick}>
              <line x1={left} y1={y} x2={left + width} y2={y} stroke="#e7ebf0" strokeWidth={1} />
              <text x={12} y={y + 4} fill="#647282" fontSize={11} fontWeight={750}>{money(tick)}</text>
            </g>
          );
        })}
        <line x1={left} y1={bottom} x2={left + width} y2={bottom} stroke="#aab4c0" strokeWidth={1.2} />
        <line x1={left} y1={top} x2={left} y2={bottom} stroke="#aab4c0" strokeWidth={1.2} />
        {values.map((value, index) => {
          const x = left + (index * slotWidth) + ((slotWidth - barWidth) / 2);
          const y = yFor(value);
          const barHeight = Math.max(2, bottom - y);
          return (
            <g key={`${labels[index]}-${index}`}>
              <rect x={x} y={bottom - barHeight} width={barWidth} height={barHeight} fill="#2b6fd6" opacity={0.86} rx={3}>
                <title>{labels[index]} - money spent: {money(value)}</title>
              </rect>
              <text x={x + (barWidth / 2)} y={326} textAnchor="middle" fill="#647282" fontSize={11} fontWeight={750}>{labels[index]}</text>
            </g>
          );
        })}
      </Box>
    </Box>
  );
}

/** Sum each point with everything before it, so the series reads as a running total. */
const cumulative = (points: number[]): number[] => {
  let total = 0;
  return points.map((value) => {
    total += value;
    return Number(total.toFixed(2));
  });
};

const varianceDatasets = (variances: VarianceRow[]) => {
  const sorted = [...variances].sort((a, b) => a.periodEnd.localeCompare(b.periodEnd));
  const labels = Array.from(new Set(sorted.map((row) => row.periodEnd))).sort();
  const types = Array.from(new Set(sorted.map((row) => row.varianceType))).slice(0, 3);
  const palette = ['#2e7d32', '#d32f2f', '#ed6c02'];
  const datasets = types.map((type, index) => ({
    label: type,
    data: labels.map((period) => {
      const row = sorted.find((candidate) => candidate.varianceType === type && candidate.periodEnd === period);
      const percentage = Number(row?.variancePercentage ?? 0);
      return Number.isFinite(percentage) ? Number(percentage.toFixed(1)) : 0;
    }),
    borderColor: palette[index % palette.length],
    backgroundColor: 'rgba(0,0,0,0.04)',
  }));
  return { labels: labels.map(monthLabel), datasets };
};

export default function ExecutiveFieldModePage({ view = 'home' }: ExecutiveFieldModePageProps) {
  const {
    dashboard,
    tasks,
    teams,
    purchaseOrders,
    inventory,
    inventoryMovements,
    paymentSummary,
    accountsTransactions,
    budgetSummary,
    cashFlow,
    variances,
    projectId,
    projectsLoading,
    projectsError,
    loading,
    error,
    failedSources,
    reload,
  } = useExecutiveFieldData();
  const user = useSelector((state: RootState) => state.auth.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [cardViews, setCardViews] = useState<Record<string, number>>({});
  const [constructionPeriodMode, setConstructionPeriodMode] = useState<AnalyticsPeriodMode>('month');
  const [constructionSelectedMonth, setConstructionSelectedMonth] = useState(monthKey());
  const [constructionSelectedWeekStart, setConstructionSelectedWeekStart] = useState(dateInputValue(addDays(new Date(), -6)));
  const [constructionSelectedDay, setConstructionSelectedDay] = useState(dateInputValue(new Date()));
  const [inspectionPeriodMode, setInspectionPeriodMode] = useState<AnalyticsPeriodMode>('month');
  const [inspectionSelectedMonth, setInspectionSelectedMonth] = useState(monthKey());
  const [inspectionSelectedWeekStart, setInspectionSelectedWeekStart] = useState(dateInputValue(addDays(new Date(), -6)));
  const [inspectionSelectedDay, setInspectionSelectedDay] = useState(dateInputValue(new Date()));
  const [selectedStoresMonth, setSelectedStoresMonth] = useState(monthKey());
  const [selectedStoresGroup, setSelectedStoresGroup] = useState('');
  const [selectedProcurementMonth, setSelectedProcurementMonth] = useState(monthKey());
  const [financialFromDate, setFinancialFromDate] = useState(dateInputValue(addMonths(new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1)), -11)));
  const [financialToDate, setFinancialToDate] = useState(monthBounds(monthKey()).end);
  const allowPersonnelSelection = ['admin', 'field_admin'].includes(user?.role ?? '');
  const selectedPersonnel = allowPersonnelSelection
    ? fieldPersonnelById(executiveFieldPersonnel, searchParams.get(ACCOUNTS_PERSONNEL_QUERY_KEY))
    : null;

  const indicators = useMemo<IndicatorCard[]>(() => {
    const cards: IndicatorCard[] = [];

    if (tasks.length > 0) {
      const completed = tasks.filter((task) => COMPLETED_STATUSES.includes(statusKey(task))).length;
      const blocked = tasks.filter((task) => statusKey(task) === 'BLOCKED').length;
      const active = tasks.filter((task) => statusKey(task) === 'IN_PROGRESS').length;
      const progress = Math.round(tasks.reduce((sum, task) => sum + Number(task.percentComplete ?? 0), 0) / tasks.length);
      cards.push({
        label: 'Construction tasks',
        views: [
          { value: `${progress}%`, status: progress >= 70 ? 'On track' : 'Watch', note: `${active} active tasks` },
          { value: String(completed), status: 'Completed', note: `of ${tasks.length} tasks` },
          { value: String(blocked), status: 'Blocked', note: blocked > 0 ? 'need action' : 'none blocked' },
        ],
      });

      const pending = tasks.filter((task) => statusKey(task) === 'INSPECTION_PENDING').length;
      const passed = tasks.filter((task) => statusKey(task) === 'INSPECTION_PASSED').length;
      const failed = tasks.filter((task) => statusKey(task) === 'INSPECTION_FAILED').length;
      const qcRate = kpiNumber(kpiByName(dashboard, 'qualityStatus')?.value)
        ?? (passed + failed > 0 ? Math.round((passed / (passed + failed)) * 100) : null);
      const inspectionViews: IndicatorView[] = [];
      if (qcRate !== null) inspectionViews.push({ value: `${qcRate}%`, status: qcRate >= 80 ? 'Stable' : 'Watch', note: 'inspection pass rate' });
      inspectionViews.push({ value: String(passed), status: 'Passed', note: 'inspections passed' });
      inspectionViews.push({ value: String(pending), status: 'Pending', note: 'awaiting inspection' });
      cards.push({ label: 'Supervisor inspections', views: inspectionViews });
    }

    const materialsBlocked = kpiByName(dashboard, 'materialsBlocked');
    const equipmentBlocked = kpiByName(dashboard, 'equipmentBlocked');
    const materialsNotReady = tasks.filter((task) => task.materialsReady === false).length;
    if (materialsBlocked || equipmentBlocked || tasks.length > 0) {
      const storesViews: IndicatorView[] = [];
      if (materialsBlocked) {
        storesViews.push({
          value: String(materialsBlocked.value ?? 0),
          status: kpiNumber(materialsBlocked.value) ? 'Watch' : 'Clear',
          note: materialsBlocked.changeLabel ?? 'tasks blocked on materials',
        });
      }
      if (equipmentBlocked) {
        storesViews.push({
          value: String(equipmentBlocked.value ?? 0),
          status: kpiNumber(equipmentBlocked.value) ? 'Watch' : 'Clear',
          note: equipmentBlocked.changeLabel ?? 'tasks blocked on equipment',
        });
      }
      if (tasks.length > 0) {
        storesViews.push({ value: String(materialsNotReady), status: materialsNotReady > 0 ? 'Watch' : 'Ready', note: 'tasks without materials ready' });
      }
      if (storesViews.length > 0) cards.push({ label: 'Stores', views: storesViews });
    }

    if (purchaseOrders.length > 0) {
      const open = purchaseOrders.filter((po) => OPEN_PO_STATUSES.includes(po.status)).length;
      const pendingApproval = purchaseOrders.filter((po) => po.status === 'pending_approval').length;
      const today = new Date().toISOString().slice(0, 10);
      const overdue = purchaseOrders.filter((po) =>
        po.expectedDeliveryDate
        && po.expectedDeliveryDate.slice(0, 10) < today
        && OPEN_PO_STATUSES.includes(po.status)
      ).length;
      cards.push({
        label: 'Procurement',
        views: [
          { value: String(open), status: 'Open', note: `of ${purchaseOrders.length} purchase orders` },
          { value: String(overdue), status: overdue > 0 ? 'Late' : 'On time', note: 'deliveries past expected date' },
          { value: String(pendingApproval), status: 'Approval', note: 'POs awaiting approval' },
        ],
      });
    }

    if (teams.length > 0) {
      const activeTeams = teams.filter((team) => team.isActive).length;
      const members = teams.reduce((sum, team) => sum + team.memberCount, 0);
      const blockedWork = teams.reduce((sum, team) => sum + team.workload.blocked, 0);
      cards.push({
        label: 'Teams',
        views: [
          { value: `${activeTeams}/${teams.length}`, status: 'Active', note: 'teams active' },
          { value: String(members), status: 'Members', note: 'across all teams' },
          { value: String(blockedWork), status: blockedWork > 0 ? 'Blocked' : 'Clear', note: 'team tasks blocked' },
        ],
      });
    }

    const pendingCert = kpiByName(dashboard, 'acceptedPendingCertValue');
    const nextDrawdown = kpiByName(dashboard, 'nextDrawdownValue');
    const qsPending = kpiByName(dashboard, 'qsLinesPendingApproval');
    const qsViews: IndicatorView[] = [];
    if (nextDrawdown) qsViews.push({ value: `$${nextDrawdown.value ?? 0}M`, status: 'Drawdown', note: nextDrawdown.changeLabel ?? 'next drawdown value' });
    if (pendingCert) qsViews.push({ value: `$${pendingCert.value ?? 0}M`, status: 'Certified', note: 'accepted pending certification' });
    if (qsPending) qsViews.push({ value: String(qsPending.value ?? 0), status: 'Review', note: 'QS lines pending approval' });
    if (qsViews.length > 0) cards.push({ label: 'QS', views: qsViews });

    const cashPosition = kpiByName(dashboard, 'cashPosition');
    if (paymentSummary || cashPosition) {
      const accountsViews: IndicatorView[] = [];
      if (cashPosition) accountsViews.push({ value: `$${cashPosition.value ?? 0}M`, status: 'Balance', note: 'cash position' });
      if (paymentSummary) {
        accountsViews.push({ value: money(paymentSummary.totalApproved), status: 'Approved', note: `${paymentSummary.approved} payments ready` });
        accountsViews.push({ value: money(paymentSummary.totalPending), status: 'Queued', note: `${paymentSummary.pending} payments pending` });
      }
      cards.push({ label: 'Accounts', views: accountsViews });
    }

    const constructionProgress = kpiByName(dashboard, 'constructionProgress');
    const spi = kpiByName(dashboard, 'spi');
    const criticalAlerts = kpiByName(dashboard, 'criticalPathAlerts');
    const pmViews: IndicatorView[] = [];
    if (kpiNumber(constructionProgress?.value) !== null) {
      const value = kpiNumber(constructionProgress?.value) as number;
      pmViews.push({ value: `${value}%`, status: value >= 70 ? 'On track' : 'Action', note: 'phase progress' });
    }
    if (kpiNumber(spi?.value) !== null) {
      const value = kpiNumber(spi?.value) as number;
      pmViews.push({ value: value.toFixed(2), status: value >= 1 ? 'Ahead' : 'Behind', note: 'schedule performance index' });
    }
    if (criticalAlerts) {
      pmViews.push({
        value: String(criticalAlerts.value ?? 0),
        status: kpiNumber(criticalAlerts.value) ? 'Alerts' : 'Clear',
        note: criticalAlerts.changeLabel ?? 'critical-path alerts',
      });
    }
    if (pmViews.length > 0) cards.push({ label: 'Project management', views: pmViews });

    const salesReceived = kpiByName(dashboard, 'salesReceived');
    const salesOutstanding = kpiByName(dashboard, 'salesOutstanding');
    const salesCompleted = kpiByName(dashboard, 'salesContractsCompleted');
    const salesViews: IndicatorView[] = [];
    if (salesReceived) salesViews.push({ value: `$${salesReceived.value ?? 0}M`, status: 'Received', note: salesReceived.changeLabel ?? 'sales received' });
    if (salesOutstanding) salesViews.push({ value: `$${salesOutstanding.value ?? 0}M`, status: 'Outstanding', note: salesOutstanding.changeLabel ?? 'sales outstanding' });
    if (salesCompleted) salesViews.push({ value: String(salesCompleted.value ?? 0), status: 'Completed', note: salesCompleted.changeLabel ?? 'contracts completed' });
    if (salesViews.length > 0) cards.push({ label: 'Sales', views: salesViews });

    if (dashboard?.healthScore) {
      const { overall, financial, quality } = dashboard.healthScore;
      cards.push({
        label: 'Overall score',
        views: [
          { value: `${overall}%`, status: overall >= 70 ? 'Healthy' : 'Pressure', note: 'project health' },
          { value: `${financial}%`, status: financial >= 70 ? 'Strong' : 'Pressure', note: 'financial health' },
          { value: `${quality}%`, status: quality >= 70 ? 'Strong' : 'Watch', note: 'quality health' },
        ],
      });
    }

    return cards;
  }, [dashboard, tasks, teams, purchaseOrders, paymentSummary]);

  const projectProgress = useMemo(() => {
    const fromDashboard = kpiNumber(kpiByName(dashboard, 'constructionProgress')?.value);
    if (fromDashboard !== null) return fromDashboard;
    if (tasks.length > 0) {
      return Math.round(tasks.reduce((sum, task) => sum + Number(task.percentComplete ?? 0), 0) / tasks.length);
    }
    return null;
  }, [dashboard, tasks]);

  const completedWorkRows = useMemo(() => (
    tasks
      .filter(completedWorkStatus)
      .flatMap((task) => {
        const date = completedDateForTask(task);
        if (!date) return [];
        return [{
          id: task.id,
          date,
        }];
      })
  ), [tasks]);

  const completionDateBounds = useMemo(() => {
    const bounds = periodBounds(
      constructionPeriodMode,
      constructionSelectedMonth,
      constructionSelectedWeekStart,
      constructionSelectedDay,
    );
    const { start, end } = bounds;
    return start <= end ? { start, end } : { start: end, end: start };
  }, [constructionPeriodMode, constructionSelectedDay, constructionSelectedMonth, constructionSelectedWeekStart]);

  const completionLabels = useMemo(() => {
    return dateLabelsBetween(completionDateBounds.start, completionDateBounds.end);
  }, [completionDateBounds]);

  const completionChart = useMemo(() => {
    const datasets = completedWorkRows.length
      ? [{
        label: 'All workers',
        data: completionLabels.map((date) => (
          completedWorkRows.filter((row) => row.date === date).length
        )),
        borderColor: '#2b6fd6',
      }]
      : [];
    return {
      labels: completionLabels.map(shortDateLabel),
      datasets,
    };
  }, [completedWorkRows, completionLabels]);

  const inspectionDateBounds = useMemo(() => {
    const bounds = periodBounds(
      inspectionPeriodMode,
      inspectionSelectedMonth,
      inspectionSelectedWeekStart,
      inspectionSelectedDay,
    );
    const { start, end } = bounds;
    return start <= end ? { start, end } : { start: end, end: start };
  }, [inspectionPeriodMode, inspectionSelectedDay, inspectionSelectedMonth, inspectionSelectedWeekStart]);

  const inspectionLabels = useMemo(() => (
    dateLabelsBetween(inspectionDateBounds.start, inspectionDateBounds.end)
  ), [inspectionDateBounds]);

  const inspectionChart = useMemo(() => {
    const rows = tasks.flatMap((task) => {
      const date = inspectionDateForTask(task);
      const result = inspectionResultForTask(task);
      if (!date || !result) return [];
      return [{ date, result }];
    });
    return {
      labels: inspectionLabels.map(shortDateLabel),
      datasets: [
        {
          label: 'Passed inspections',
          data: inspectionLabels.map((date) => rows.filter((row) => row.date === date && row.result === 'passed').length),
          borderColor: '#2b6fd6',
        },
        {
          label: 'Failed inspections',
          data: inspectionLabels.map((date) => rows.filter((row) => row.date === date && row.result === 'failed').length),
          borderColor: '#c94a4a',
        },
      ],
      totalPassed: rows.filter((row) => row.result === 'passed').length,
      totalFailed: rows.filter((row) => row.result === 'failed').length,
    };
  }, [inspectionLabels, tasks]);

  const executiveMonthOptions = useMemo(() => monthOptions(), []);

  const periodControls = ({
    mode,
    month,
    weekStart,
    day,
    onModeChange,
    onMonthChange,
    onWeekStartChange,
    onDayChange,
  }: {
    mode: AnalyticsPeriodMode;
    month: string;
    weekStart: string;
    day: string;
    onModeChange: (mode: AnalyticsPeriodMode) => void;
    onMonthChange: (month: string) => void;
    onWeekStartChange: (date: string) => void;
    onDayChange: (date: string) => void;
  }) => (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
      <Box sx={{ display: 'flex', border: '1px solid', borderColor: '#d8dee6', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff', height: 40 }}>
        {[
          ['month', 'Month'],
          ['week', 'Week'],
          ['day', 'Day'],
        ].map(([value, label]) => (
          <Button
            key={value}
            onClick={() => onModeChange(value as AnalyticsPeriodMode)}
            sx={{
              borderRadius: 0,
              px: 1.5,
              minWidth: 78,
              color: mode === value ? '#fff' : '#17202a',
              bgcolor: mode === value ? '#17202a' : '#fff',
              borderRight: value === 'day' ? 0 : '1px solid #d8dee6',
              fontWeight: 900,
              '&:hover': { bgcolor: mode === value ? '#17202a' : '#f5f7fa' },
            }}
          >
            {label}
          </Button>
        ))}
      </Box>
      {mode === 'month' && (
        <Box
          component="select"
          value={month}
          onChange={(event) => onMonthChange(event.target.value)}
          sx={{ height: 40, minWidth: 180, px: 1.5, borderRadius: 1, border: '1px solid #d8dee6', bgcolor: '#fff', color: '#17202a', fontWeight: 900 }}
        >
          {executiveMonthOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </Box>
      )}
      {mode === 'week' && (
        <Box
          component="input"
          type="date"
          value={weekStart}
          onChange={(event) => onWeekStartChange(event.target.value)}
          sx={{ height: 40, px: 1.5, borderRadius: 1, border: '1px solid #d8dee6', bgcolor: '#fff', color: '#17202a', fontWeight: 900 }}
        />
      )}
      {mode === 'day' && (
        <Box
          component="input"
          type="date"
          value={day}
          onChange={(event) => onDayChange(event.target.value)}
          sx={{ height: 40, px: 1.5, borderRadius: 1, border: '1px solid #d8dee6', bgcolor: '#fff', color: '#17202a', fontWeight: 900 }}
        />
      )}
    </Stack>
  );

  const storesGroups = useMemo(() => (
    Array.from(new Set(inventory.map(inventoryGroupForRow))).sort((a, b) => a.localeCompare(b))
  ), [inventory]);

  const activeStoresGroup = selectedStoresGroup || storesGroups[0] || '';

  const storesStockChart = useMemo(() => {
    if (!activeStoresGroup) return { labels: [] as string[], datasets: [] as CrewCompletionDataset[], itemCount: 0 };
    const bounds = monthBounds(selectedStoresMonth);
    const start = new Date(`${bounds.start}T00:00:00Z`);
    const end = new Date(`${bounds.end}T00:00:00Z`);
    const rows = inventory.filter((row) => inventoryGroupForRow(row) === activeStoresGroup);
    const resourceIds = new Set(rows.map((row) => row.resourceId));
    const monthMovements = inventoryMovements
      .filter((movement) => resourceIds.has(movement.resourceId))
      .filter((movement) => {
        const date = movementDate(movement);
        return date !== null && date >= bounds.start && date <= bounds.end;
      });
    const totalCurrentStock = rows.reduce((sum, row) => sum + Number(row.totalQuantity ?? row.quantityAvailable ?? 0), 0);
    const monthDelta = monthMovements.reduce((sum, movement) => sum + movementSignedQuantity(movement), 0);
    let runningTotal = Math.max(0, totalCurrentStock - monthDelta);
    const labels: string[] = [];
    const data: number[] = [];
    for (let current = start; current <= end; current = addDays(current, 1)) {
      const date = dateInputValue(current);
      runningTotal += monthMovements
        .filter((movement) => movementDate(movement) === date)
        .reduce((sum, movement) => sum + movementSignedQuantity(movement), 0);
      labels.push(shortDateLabel(date));
      data.push(Number(Math.max(0, runningTotal).toFixed(2)));
    }
    return {
      labels,
      datasets: [{ label: activeStoresGroup, data, borderColor: '#2b6fd6' }],
      itemCount: rows.length,
    };
  }, [activeStoresGroup, inventory, inventoryMovements, selectedStoresMonth]);

  const supplierDeliveryChart = useMemo(() => {
    const bounds = monthBounds(selectedProcurementMonth);
    const start = new Date(`${bounds.start}T00:00:00Z`);
    const end = new Date(`${bounds.end}T00:00:00Z`);
    const orders = purchaseOrders.filter((po) => {
      const date = purchaseOrderDate(po);
      return po.status !== 'cancelled'
        && date !== null
        && date >= bounds.start
        && date <= bounds.end;
    });
    const labels: string[] = [];
    const data: number[] = [];
    for (let current = start; current <= end; current = addDays(current, 1)) {
      const date = dateInputValue(current);
      labels.push(shortDateLabel(date));
      data.push(orders.filter((po) => purchaseOrderDate(po) === date).length);
    }
    return {
      labels,
      datasets: [{ label: 'All suppliers', data, borderColor: '#1f9d6a' }],
      orderCount: orders.length,
    };
  }, [purchaseOrders, selectedProcurementMonth]);

  const financialSpendingRows = useMemo(() => (
    accountsTransactions.filter((tx: AccountsTransaction) => (
      tx.type !== 'revenue'
      && ['recorded', 'reconciled'].includes(tx.status)
      && monthKeyForDate(tx.transactionDate) !== 'undated'
    ))
  ), [accountsTransactions]);

  const financialSpendingChart = useMemo(() => {
    const months = monthKeysBetween(financialFromDate, financialToDate);
    const start = new Date(`${financialFromDate}T00:00:00Z`);
    const end = new Date(`${financialToDate}T23:59:59Z`);
    const [from, to] = start <= end ? [start, end] : [end, start];
    const rows = financialSpendingRows.filter((tx) => {
      const dateOnly = dateOnlyTime(tx.transactionDate);
      if (dateOnly !== null) return dateOnly >= from.getTime() && dateOnly <= to.getTime();
      const date = new Date(tx.transactionDate);
      return !Number.isNaN(date.getTime()) && date >= from && date <= to;
    });
    const values = months.map((month) => rows
      .filter((tx) => monthKeyForDate(tx.transactionDate) === month)
      .reduce((sum, tx) => sum + Number(tx.amount || 0), 0));
    return {
      labels: months.map(shortMonthLabelFor),
      values,
      total: values.reduce((sum, value) => sum + value, 0),
      transactionCount: rows.length,
    };
  }, [financialFromDate, financialSpendingRows, financialToDate]);

  const setFinancialSpendingPeriod = (period: 'month' | 'quarter' | 'year' | 'all') => {
    const now = new Date();
    if (period === 'month') {
      const bounds = monthBounds(monthKey(now));
      setFinancialFromDate(bounds.start);
      setFinancialToDate(bounds.end);
      return;
    }
    if (period === 'quarter') {
      setFinancialFromDate(dateInputValue(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 2, 1))));
      setFinancialToDate(monthBounds(monthKey(now)).end);
      return;
    }
    if (period === 'year') {
      setFinancialFromDate(dateInputValue(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 11, 1))));
      setFinancialToDate(monthBounds(monthKey(now)).end);
      return;
    }
    const datedRows = financialSpendingRows
      .map((tx) => {
        const dateOnly = dateOnlyParts(tx.transactionDate);
        return dateOnly
          ? new Date(dateOnly.year, dateOnly.month - 1, dateOnly.day)
          : new Date(tx.transactionDate);
      })
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    const first = datedRows[0] ?? now;
    const last = datedRows[datedRows.length - 1] ?? now;
    setFinancialFromDate(dateInputValue(new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1))));
    setFinancialToDate(monthBounds(monthKey(last)).end);
  };

  const flipCard = (label: string, viewCount: number, direction: 1 | -1) => {
    setCardViews((current) => ({
      ...current,
      [label]: ((current[label] ?? 0) + direction + viewCount) % viewCount,
    }));
  };

  const metricCard = (item: IndicatorCard) => {
    const activeIndex = (cardViews[item.label] ?? 0) % item.views.length;
    const active = item.views[activeIndex];
    const targetPathByLabel: Record<string, string> = {
      'Construction tasks': '/field/executive/construction-tasks',
      'Supervisor inspections': '/field/executive/supervisor-inspections',
      Stores: '/field/executive/stores',
      Procurement: '/field/executive/procurement',
    };
    const targetPath = targetPathByLabel[item.label];
    return (
      <Paper
        component={targetPath ? RouterLink : 'div'}
        to={targetPath}
        variant="outlined"
        sx={{
          p: 1,
          borderRadius: 1,
          minHeight: 124,
          overflow: 'hidden',
          color: 'inherit',
          textDecoration: 'none',
          cursor: targetPath ? 'pointer' : 'default',
          display: 'block',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
          <IconButton
            size="small"
            aria-label={`Previous ${item.label} indicator`}
            onClick={(event) => {
              event.preventDefault();
              flipCard(item.label, item.views.length, -1);
            }}
            sx={{ p: 0.25 }}
          >
            <KeyboardArrowLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', textAlign: 'center', lineHeight: 1.1, minWidth: 0 }}>
            {item.label}
          </Typography>
          <IconButton
            size="small"
            aria-label={`Next ${item.label} indicator`}
            onClick={(event) => {
              event.preventDefault();
              flipCard(item.label, item.views.length, 1);
            }}
            sx={{ p: 0.25 }}
          >
            <KeyboardArrowRightIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.1, mt: 0.5, textAlign: 'center' }}>{active.value}</Typography>
        <Stack alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
          <Chip size="small" label={active.status} />
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>{active.note}</Typography>
          <Typography variant="caption" color="text.secondary">{activeIndex + 1}/{item.views.length}</Typography>
        </Stack>
      </Paper>
    );
  };

  const statusBanner = (
    <>
      {loading && (
        <Chip icon={<CircularProgress size={16} />} label="Loading executive data" sx={{ alignSelf: 'flex-start' }} />
      )}
      {error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>
          {error}
        </Alert>
      )}
      {!error && projectsError && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>
          {projectsError}
        </Alert>
      )}
      {!error && failedSources.length > 0 && (
        <Alert severity="warning" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>
          Some data could not be loaded: {failedSources.join(', ')}.
        </Alert>
      )}
    </>
  );

  const openSelectedPersonnel = (personnelId: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', 'executive');
    nextParams.set(ACCOUNTS_PERSONNEL_QUERY_KEY, personnelId);
    setSearchParams(nextParams);
  };

  const clearSelectedPersonnel = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', 'executive');
    nextParams.delete(ACCOUNTS_PERSONNEL_QUERY_KEY);
    setSearchParams(nextParams);
  };

  const personnelIndicatorGrid = (
    <Stack spacing={1.5}>
      <Typography variant="h4" fontWeight={900}>Executive Personnel</Typography>
      <Grid container spacing={1.5}>
        {executiveFieldPersonnel.map((person) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={person.id}>
            <PersonnelFolderCard
              name={person.name}
              email={person.email}
              credentialHint={person.credentialHint}
              onClick={() => openSelectedPersonnel(person.id)}
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );

  const personnelContextCard = selectedPersonnel ? (
    <Paper sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">Executive</Typography>
          <Typography variant="h5" fontWeight={900}>{selectedPersonnel.name}</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
            <Chip size="small" label={`Login: ${selectedPersonnel.email}`} />
            <Chip size="small" label={selectedPersonnel.credentialHint} />
            <Chip size="small" label={selectedPersonnel.team} />
          </Stack>
        </Box>
        <Button size="small" variant="outlined" onClick={clearSelectedPersonnel} sx={{ borderRadius: 1, fontWeight: 900 }}>
          Back to Executive Personnel
        </Button>
      </Stack>
    </Paper>
  ) : null;

  if (view === 'home' && allowPersonnelSelection && !selectedPersonnel) {
    return (
      <Stack spacing={1.5}>
        {statusBanner}
        {personnelIndicatorGrid}
      </Stack>
    );
  }

  if (view === 'supervisor-inspections') {
    const hasInspectionData = inspectionChart.totalPassed + inspectionChart.totalFailed > 0;
    return (
      <Stack spacing={1.5}>
        {personnelContextCard}
        <Typography variant="h4" fontWeight={900}>Executive</Typography>
        <Typography variant="h6" fontWeight={900}>Supervisor Inspections</Typography>
        {statusBanner}
        {!projectId && !projectsLoading && (
          <Alert severity="warning">Select an active project to load executive supervisor inspections.</Alert>
        )}

        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 1 }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', lg: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h5" fontWeight={900} sx={{ lineHeight: 1.2 }}>Inspection Analytics</Typography>
              <Typography color="text.secondary" fontWeight={650}>Passed and failed inspections totaled across all supervisors.</Typography>
            </Box>
            {periodControls({
              mode: inspectionPeriodMode,
              month: inspectionSelectedMonth,
              weekStart: inspectionSelectedWeekStart,
              day: inspectionSelectedDay,
              onModeChange: setInspectionPeriodMode,
              onMonthChange: setInspectionSelectedMonth,
              onWeekStartChange: setInspectionSelectedWeekStart,
              onDayChange: setInspectionSelectedDay,
            })}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 1, bgcolor: '#fff', borderColor: '#d8dee6', boxShadow: '0 10px 22px rgba(22, 32, 42, .05)', overflow: 'hidden' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} sx={{ p: 2, pb: 0 }}>
            <Box>
              <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.2 }}>Passed vs Failed Inspections</Typography>
              <Typography color="text.secondary" fontWeight={650}>Daily inspection results from {shortDateLabel(inspectionDateBounds.start)} to {shortDateLabel(inspectionDateBounds.end)}.</Typography>
            </Box>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              {inspectionChart.datasets.map((dataset) => (
                <Stack key={dataset.label} direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 18, height: 4, borderRadius: 99, bgcolor: dataset.borderColor }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>{dataset.label}</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
          {hasInspectionData ? (
            <CrewCompletionSvgChart labels={inspectionChart.labels} datasets={inspectionChart.datasets} spanSinglePoint />
          ) : (
            !loading && <Box sx={{ p: 2 }}><Alert severity="info">No passed or failed inspections are recorded for the selected period.</Alert></Box>
          )}
        </Paper>
      </Stack>
    );
  }

  if (view === 'construction-tasks') {
    return (
      <Stack spacing={1.5}>
        {personnelContextCard}
        <Typography variant="h4" fontWeight={900}>Executive</Typography>
        <Typography variant="h6" fontWeight={900}>Construction Tasks</Typography>
        {statusBanner}
        {!projectId && !projectsLoading && (
          <Alert severity="warning">Select an active project to load executive construction tasks.</Alert>
        )}

        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 1 }}>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', lg: 'center' }} justifyContent="space-between">
              <Stack spacing={0.5}>
                <Typography variant="h5" fontWeight={900} sx={{ lineHeight: 1.2 }}>Worker Analytics</Typography>
                <Typography color="text.secondary" fontWeight={650}>Daily completed work for all workers together, based on tasks passed by supervisor inspection.</Typography>
              </Stack>
              {periodControls({
                mode: constructionPeriodMode,
                month: constructionSelectedMonth,
                weekStart: constructionSelectedWeekStart,
                day: constructionSelectedDay,
                onModeChange: setConstructionPeriodMode,
                onMonthChange: setConstructionSelectedMonth,
                onWeekStartChange: setConstructionSelectedWeekStart,
                onDayChange: setConstructionSelectedDay,
              })}
            </Stack>

            <Alert severity="info">
              Showing {constructionPeriodMode} view from {shortDateLabel(completionDateBounds.start)} to {shortDateLabel(completionDateBounds.end)}.
            </Alert>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 1, bgcolor: '#fff', borderColor: '#d8dee6', boxShadow: '0 10px 22px rgba(22, 32, 42, .05)', overflow: 'hidden' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} sx={{ p: 2, pb: 0 }}>
            <Box>
              <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.2 }}>Daily Work Completed</Typography>
              <Typography color="text.secondary" fontWeight={650}>The line tracks total daily completed work for every day in the selected period.</Typography>
            </Box>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              {completionChart.datasets.map((dataset) => (
                <Stack key={dataset.label} direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 18, height: 4, borderRadius: 99, bgcolor: dataset.borderColor }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>{dataset.label}</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>

          {completionChart.datasets.length > 0 ? (
            <CrewCompletionSvgChart
              labels={completionChart.labels}
              datasets={completionChart.datasets}
              ariaLabel="Line graph showing total daily completed work for all workers"
            />
            ) : (
              !loading && (
                <Box sx={{ p: 2 }}>
                  <Alert severity="info">No completed construction tasks are recorded for the selected date filter.</Alert>
                </Box>
              )
            )}

        </Paper>
      </Stack>
    );
  }

  if (view === 'stores') {
    return (
      <Stack spacing={1.5}>
        {personnelContextCard}
        <Typography variant="h4" fontWeight={900}>Executive</Typography>
        <Typography variant="h6" fontWeight={900}>Stores</Typography>
        {statusBanner}
        {!projectId && !projectsLoading && (
          <Alert severity="warning">Select an active project to load executive stores analytics.</Alert>
        )}

        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h5" fontWeight={900} sx={{ lineHeight: 1.2 }}>Stores Stock Usage</Typography>
              <Typography color="text.secondary" fontWeight={650}>Select an inventory monitoring menu and month to track stock usage and recouping.</Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Box
                component="select"
                value={activeStoresGroup}
                onChange={(event) => setSelectedStoresGroup(event.target.value)}
                sx={{ height: 40, minWidth: 230, px: 1.5, borderRadius: 1, border: '1px solid #d8dee6', bgcolor: '#fff', color: '#17202a', fontWeight: 900 }}
              >
                {storesGroups.map((group) => <option key={group} value={group}>{group}</option>)}
              </Box>
              <Box
                component="select"
                value={selectedStoresMonth}
                onChange={(event) => setSelectedStoresMonth(event.target.value)}
                sx={{ height: 40, minWidth: 180, px: 1.5, borderRadius: 1, border: '1px solid #d8dee6', bgcolor: '#fff', color: '#17202a', fontWeight: 900 }}
              >
                {executiveMonthOptions.map((month) => <option key={month.value} value={month.value}>{month.label}</option>)}
              </Box>
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 1, bgcolor: '#fff', borderColor: '#d8dee6', boxShadow: '0 10px 22px rgba(22, 32, 42, .05)', overflow: 'hidden' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} sx={{ p: 2, pb: 0 }}>
            <Box>
              <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.2 }}>Monthly Stock Level</Typography>
              <Typography color="text.secondary" fontWeight={650}>The line decreases as stock is issued or consumed and rises when stock is received or returned.</Typography>
            </Box>
            {storesStockChart.datasets.map((dataset) => (
              <Stack key={dataset.label} direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 18, height: 4, borderRadius: 99, bgcolor: dataset.borderColor }} />
                <Typography variant="caption" color="text.secondary" fontWeight={800}>{dataset.label} · {storesStockChart.itemCount} stock item{storesStockChart.itemCount === 1 ? '' : 's'}</Typography>
              </Stack>
            ))}
          </Stack>
          {storesStockChart.datasets.length > 0 ? (
            <CrewCompletionSvgChart labels={storesStockChart.labels} datasets={storesStockChart.datasets} spanSinglePoint />
          ) : (
            !loading && <Box sx={{ p: 2 }}><Alert severity="info">No inventory categories are available for Stores yet.</Alert></Box>
          )}
        </Paper>
      </Stack>
    );
  }

  if (view === 'procurement') {
    return (
      <Stack spacing={1.5}>
        {personnelContextCard}
        <Typography variant="h4" fontWeight={900}>Executive</Typography>
        <Typography variant="h6" fontWeight={900}>Procurement</Typography>
        {statusBanner}
        {!projectId && !projectsLoading && (
          <Alert severity="warning">Select an active project to load executive procurement analytics.</Alert>
        )}

        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h5" fontWeight={900} sx={{ lineHeight: 1.2 }}>Supplier Delivery Performance</Typography>
              <Typography color="text.secondary" fontWeight={650}>Select a month to track all supplier purchase orders together.</Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Box
                component="select"
                value={selectedProcurementMonth}
                onChange={(event) => setSelectedProcurementMonth(event.target.value)}
                sx={{ height: 40, minWidth: 180, px: 1.5, borderRadius: 1, border: '1px solid #d8dee6', bgcolor: '#fff', color: '#17202a', fontWeight: 900 }}
              >
                {executiveMonthOptions.map((month) => <option key={month.value} value={month.value}>{month.label}</option>)}
              </Box>
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 1, bgcolor: '#fff', borderColor: '#d8dee6', boxShadow: '0 10px 22px rgba(22, 32, 42, .05)', overflow: 'hidden' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} sx={{ p: 2, pb: 0 }}>
            <Box>
              <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.2 }}>Purchase Orders Placed</Typography>
              <Typography color="text.secondary" fontWeight={650}>Each bar shows all supplier purchase orders placed on that day.</Typography>
            </Box>
            {supplierDeliveryChart.datasets.map((dataset) => (
              <Stack key={dataset.label} direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 18, height: 4, borderRadius: 99, bgcolor: dataset.borderColor }} />
                <Typography variant="caption" color="text.secondary" fontWeight={800}>{dataset.label} · {supplierDeliveryChart.orderCount} purchase order{supplierDeliveryChart.orderCount === 1 ? '' : 's'}</Typography>
              </Stack>
            ))}
          </Stack>
          {supplierDeliveryChart.datasets.length > 0 ? (
            <SupplierDeliveryBarChart labels={supplierDeliveryChart.labels} dataset={supplierDeliveryChart.datasets[0]} />
          ) : (
            !loading && <Box sx={{ p: 2 }}><Alert severity="info">No supplier purchase orders are available for this month yet.</Alert></Box>
          )}
        </Paper>
      </Stack>
    );
  }

  if (view === 'financial') {
    const labels = cashFlow.map((point) => point.month);
    const varianceChart = varianceDatasets(variances);
    return (
      <Stack spacing={1.5}>
        {personnelContextCard}
        <Typography variant="h4" fontWeight={900}>Executive</Typography>
        <Typography variant="h6" fontWeight={900}>Financial Analysis</Typography>
        {statusBanner}
        {!projectId && !projectsLoading && (
          <Alert severity="warning">Select an active project to load executive financials.</Alert>
        )}

        <Paper variant="outlined" sx={{ borderRadius: 1, bgcolor: '#fff', borderColor: '#d8dee6', boxShadow: '0 10px 22px rgba(22, 32, 42, .05)', overflow: 'hidden' }}>
          <Stack spacing={1.5} sx={{ p: 2, pb: 0 }}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
              <Box>
                <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.2 }}>Monthly Money Spent</Typography>
                <Typography color="text.secondary" fontWeight={650}>
                  Total paid spending by month across task pay, PO pay, and other account payments.
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
                {[
                  ['month', 'Month'],
                  ['quarter', 'Quarter'],
                  ['year', 'Year'],
                  ['all', 'All'],
                ].map(([value, label]) => (
                  <Button
                    key={value}
                    size="small"
                    variant="outlined"
                    onClick={() => setFinancialSpendingPeriod(value as 'month' | 'quarter' | 'year' | 'all')}
                    sx={{ borderRadius: 1, fontWeight: 900 }}
                  >
                    {label}
                  </Button>
                ))}
              </Stack>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap" useFlexGap>
              <Box
                component="input"
                type="date"
                aria-label="Monthly spending from date"
                value={financialFromDate}
                onChange={(event) => setFinancialFromDate(event.target.value)}
                sx={{ height: 40, px: 1.5, borderRadius: 1, border: '1px solid #d8dee6', bgcolor: '#fff', color: '#17202a', fontWeight: 900 }}
              />
              <Box
                component="input"
                type="date"
                aria-label="Monthly spending to date"
                value={financialToDate}
                onChange={(event) => setFinancialToDate(event.target.value)}
                sx={{ height: 40, px: 1.5, borderRadius: 1, border: '1px solid #d8dee6', bgcolor: '#fff', color: '#17202a', fontWeight: 900 }}
              />
              <Chip size="small" color="primary" label={`${money(financialSpendingChart.total)} spent`} />
              <Chip size="small" variant="outlined" label={`${financialSpendingChart.transactionCount} paid transaction${financialSpendingChart.transactionCount === 1 ? '' : 's'}`} />
            </Stack>
          </Stack>
          {financialSpendingChart.labels.length > 0 ? (
            <MonthlySpendingBarChart labels={financialSpendingChart.labels} values={financialSpendingChart.values} />
          ) : (
            !loading && <Box sx={{ p: 2 }}><Alert severity="info">Enter a valid date range to show monthly spending.</Alert></Box>
          )}
        </Paper>

        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={900}>Overview</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {cashFlow.length > 0 ? (
              <>
                <LineChart
                  labels={labels}
                  height={220}
                  yAxisLabel="JMD"
                  datasets={[
                    { label: 'Projected (cumulative)', data: cumulative(cashFlow.map((point) => point.projected)), borderColor: '#1976d2', backgroundColor: 'rgba(25,118,210,0.12)', fill: true },
                    { label: 'Actual (cumulative)', data: cumulative(cashFlow.map((point) => point.actual)), borderColor: '#2e7d32', backgroundColor: 'rgba(46,125,50,0.10)', fill: true },
                  ]}
                />
                <Typography variant="caption" color="text.secondary">
                  Cumulative net cash flow over the last {cashFlow.length} months.
                  {budgetSummary ? ` Budget utilisation ${Math.round(budgetSummary.utilizationPercentage)}% (${money(budgetSummary.totalSpent)} of ${money(budgetSummary.totalAllocated)}).` : ''}
                </Typography>
              </>
            ) : (
              !loading && <Alert severity="info">No cash-flow history is recorded for this project yet.</Alert>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={900}>Cash Flow</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {cashFlow.length > 0 ? (
              <>
                <LineChart
                  labels={labels}
                  height={220}
                  yAxisLabel="JMD"
                  datasets={[
                    { label: 'Projected', data: cashFlow.map((point) => point.projected), borderColor: '#1976d2', backgroundColor: 'rgba(25,118,210,0.10)', fill: true },
                    { label: 'Actual', data: cashFlow.map((point) => point.actual), borderColor: '#ed6c02', backgroundColor: 'rgba(237,108,2,0.10)', fill: true },
                  ]}
                />
                <Typography variant="caption" color="text.secondary">Monthly net cash flow, projected against actual.</Typography>
              </>
            ) : (
              !loading && <Alert severity="info">No cash-flow history is recorded for this project yet.</Alert>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={900}>Variances</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {varianceChart.datasets.length > 0 ? (
              <>
                <LineChart
                  labels={varianceChart.labels}
                  height={220}
                  yAxisLabel="Variance %"
                  datasets={varianceChart.datasets}
                />
                <Typography variant="caption" color="text.secondary">Negative variance highlights cost pressure against planned allowance.</Typography>
              </>
            ) : (
              !loading && <Alert severity="info">No variance records exist for this project yet.</Alert>
            )}
          </AccordionDetails>
        </Accordion>
      </Stack>
    );
  }

  if (view === 'estate-progress') {
    return (
      <Suspense fallback={<Typography color="text.secondary">Loading estate progress…</Typography>}>
        <ExecutiveEstateProgress />
      </Suspense>
    );
  }

  return (
    <Stack spacing={1.5}>
      {personnelContextCard}
      <Typography variant="h4" fontWeight={900}>Executive</Typography>
      {statusBanner}

      {indicators.length > 0 ? (
        <Grid container spacing={1}>
          {indicators.map((indicator) => (
            <Grid item xs={6} md={3} key={indicator.label}>
              {metricCard(indicator)}
            </Grid>
          ))}
        </Grid>
      ) : (
        !loading && !error && <Alert severity="info">No project data is available to summarise yet.</Alert>
      )}

      {projectProgress !== null && (
        <Box>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">Project Progress</Typography>
            <Typography variant="caption" fontWeight={800}>{projectProgress}%</Typography>
          </Stack>
          <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, projectProgress))} sx={{ height: 8, borderRadius: 1, mt: 0.5 }} />
        </Box>
      )}

      <Button
        component={RouterLink}
        to="/field/executive/construction-tasks"
        fullWidth
        variant="outlined"
        sx={{ minHeight: 72, borderRadius: 1, justifyContent: 'space-between', px: 2, color: 'text.primary', borderColor: 'divider' }}
      >
        <Typography fontWeight={900}>Construction Tasks</Typography>
        <Typography fontWeight={900}>Open</Typography>
      </Button>

      <Button
        component={RouterLink}
        to="/field/executive/supervisor-inspections"
        fullWidth
        variant="outlined"
        sx={{ minHeight: 72, borderRadius: 1, justifyContent: 'space-between', px: 2, color: 'text.primary', borderColor: 'divider' }}
      >
        <Typography fontWeight={900}>Supervisor Inspections</Typography>
        <Typography fontWeight={900}>Open</Typography>
      </Button>

      <Button
        component={RouterLink}
        to="/field/executive/stores"
        fullWidth
        variant="outlined"
        sx={{ minHeight: 72, borderRadius: 1, justifyContent: 'space-between', px: 2, color: 'text.primary', borderColor: 'divider' }}
      >
        <Typography fontWeight={900}>Stores</Typography>
        <Typography fontWeight={900}>Open</Typography>
      </Button>

      <Button
        component={RouterLink}
        to="/field/executive/procurement"
        fullWidth
        variant="outlined"
        sx={{ minHeight: 72, borderRadius: 1, justifyContent: 'space-between', px: 2, color: 'text.primary', borderColor: 'divider' }}
      >
        <Typography fontWeight={900}>Procurement</Typography>
        <Typography fontWeight={900}>Open</Typography>
      </Button>

      <Button
        component={RouterLink}
        to="/field/executive/financial"
        fullWidth
        variant="outlined"
        sx={{ minHeight: 72, borderRadius: 1, justifyContent: 'space-between', px: 2, color: 'text.primary', borderColor: 'divider' }}
      >
        <Typography fontWeight={900}>Financial Analysis</Typography>
        <Typography fontWeight={900}>Open</Typography>
      </Button>
    </Stack>
  );
}
